import React, { useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { be_regions, fr_regions, lu_regions } from "bundles/common/utils/constants";
import { formatRegionMessage } from "bundles/common/utils/utils";

export const EXPERIENCE_YEAR_OPTIONS = [
  { label: "0 – 2 years",  value: "0-2",   gte: 0,  lte: 2  },
  { label: "3 – 5 years",  value: "3-5",   gte: 3,  lte: 5  },
  { label: "6 – 10 years", value: "6-10",  gte: 6,  lte: 10 },
  { label: "11+ years",    value: "11+",   gte: 11, lte: 99 },
];

const SECTOR_OPTIONS = [
  { label: "Architecture", value: "architecture" },
  { label: "Engineering", value: "engineering" },
  { label: "Construction", value: "construction" },
  { label: "Administration", value: "administration" },
  { label: "Industry", value: "industry" },
];

export const LANGUAGE_OPTIONS = [
  { label: "French",  value: "fr", locales: ["fr", "fr-fr"] },
  { label: "English", value: "en", locales: ["en"] },
  { label: "Dutch",   value: "nl", locales: ["nl"] },
];

export const PROFILE_COMPLETION_OPTIONS = [
  { label: "0 – 25%",    value: "0-25",   gte: 0,  lte: 25  },
  { label: "26 – 50%",   value: "26-50",  gte: 26, lte: 50  },
  { label: "51 – 75%",   value: "51-75",  gte: 51, lte: 75  },
  { label: "76 – 100%",  value: "76-100", gte: 76, lte: 100 },
];

const chipSx = (active) => ({
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "0.6rem",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "opacity 0.15s ease",
  ...(active
    ? {
      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
      color: "hsl(150 38% 17%)",
      borderColor: "hsl(150 38% 17%)",
      "&:hover": { opacity: 0.8 },
    }
    : {
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
      borderColor: "hsl(140 8% 88%)",
      "&:hover": {
        borderColor: "hsl(140 8% 82%)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const options = def.grouped
    ? def.groups.flatMap((g) => g.options)
    : def.options;

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {/* Options */}
          {def.grouped
            ? def.groups.map((group) => (
              <Box key={group.label}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {group.label}
                </Typography>
                {group.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                    control={
                      <Checkbox
                        size="small"
                        disableRipple
                        checked={selected.includes(opt.value)}
                        onChange={() => handleToggle(opt.value)}
                        sx={{
                          color: "hsl(140 8% 82%)",
                          "&.Mui-checked": { color: "hsl(150 38% 17%)" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {opt.label}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            ))
            : options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    disableRipple
                    checked={selected.includes(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                    sx={{
                      color: "hsl(140 8% 82%)",
                      "&.Mui-checked": { color: "hsl(150 38% 17%)" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {opt.label}
                  </Typography>
                }
              />
            ))}
        </Box>
      </Popover>
    </>
  );
};

{/* Main Filter bar */}
const CandidateTableFilters = ({ filters, onFiltersChange, creators = [] }) => {
  const intl = useIntl();

  const filterDefs = useMemo(
    () => [
      {
        id: "experience_year",
        label: intl.formatMessage({ id: "internal.filters.experience", defaultMessage: "Experience" }),
        options: EXPERIENCE_YEAR_OPTIONS,
      },
      ...(creators.length
        ? [{
          id: "created_by",
          label: intl.formatMessage({ id: "internal.filters.created_by", defaultMessage: "Created by" }),
          options: creators.map((c) => ({ value: c.id, label: c.full_name })),
        }]
        : []),
      {
        id: "sector",
        label: intl.formatMessage({ id: "internal.filters.sector", defaultMessage: "Sector" }),
        options: SECTOR_OPTIONS,
      },
      {
        id: "languages",
        label: intl.formatMessage({ id: "internal.filters.languages", defaultMessage: "Languages" }),
        options: LANGUAGE_OPTIONS,
      },
      {
        id: "region",
        label: intl.formatMessage({ id: "candidate.region", defaultMessage: "Region" }),
        grouped: true,
        groups: [
          {
            label: formatRegionMessage("belgium"),
            options: be_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("luxembourg"),
            options: lu_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("france"),
            options: fr_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
        ],
      },
      {
        id: "profile_completion",
        label: intl.formatMessage({ id: "internal.filters.profile_completion", defaultMessage: "Profile completion" }),
        options: PROFILE_COMPLETION_OPTIONS,
      },
    ],
    [intl, creators],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, arr) => sum + arr.length, 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_FILTERS });
  }, [onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid hsl(140 8% 88%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "hsl(150 30% 12%)", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "hsl(0 72% 51%) !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(0 72% 51%)",
              borderColor: "hsl(0 72% 51%)",
              backgroundColor: "transparent",
              height: 28,
              borderRadius: "0.6rem",
              transition: "opacity 0.15s ease",
              "&:hover": { backgroundColor: "hsla(0, 72%, 51%, 0.06)", opacity: 0.9 },
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

/** Pristine (empty) filters state object. */
export const EMPTY_FILTERS = {
  experience_year: [],
  sector: [],
  languages: [],
  region: [],
  profile_completion: [],
  created_by: [],
};

export default CandidateTableFilters;
